<script lang="ts">
  import { goto } from '@mateothegreat/svelte5-router'
  import SearchIcon from '@lucide/svelte/icons/search'
  import LoaderCircle from '@lucide/svelte/icons/loader-circle'
  import ServerIcon from '@lucide/svelte/icons/server'
  import LibraryIcon from '@lucide/svelte/icons/library'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import * as Command from '$lib/components/ui/command/index.js'
  import * as Popover from '$lib/components/ui/popover/index.js'
  import { searchSeriesGlobal } from '$lib/apis/library/api'
  import { servers } from '$lib/store'
  import { filterNsfwSeries, prefs } from '$lib/preferences.svelte'
  import { formatServerName, serverHash } from '$lib/utils'
  import PLACEHOLDER from '$assets/icons/book/cover_placeholder.svg'

  const DEBOUNCE_MS = 250

  let {
    libraries = {},
  }: {
    libraries?: Record<string, any[]>
  } = $props()

  let open = $state(false)
  let query = $state('')
  let results = $state<any[]>([])
  let loading = $state(false)
  let searched = $state(false)
  let inputEl = $state<HTMLInputElement | null>(null)

  const serverList = $derived(Object.values($servers) as any[])

  function libraryName(mediaId: string | undefined, libraryId: string | undefined) {
    if (!mediaId || !libraryId) return undefined
    const libs = libraries?.[mediaId] ?? []
    return libs.find((l: any) => l?.id === libraryId)?.name as string | undefined
  }

  const filtered = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(results)
  })

  const emptyHint = $derived.by(() => {
    if (!serverList.length) return 'No servers connected.'
    if (!query.trim()) return `Type to search across ${serverList.length} server${serverList.length === 1 ? '' : 's'}.`
    if (loading) return ''
    if (!searched) return ''
    return 'No matching series.'
  })

  $effect(() => {
    if (!open) return
    const id = requestAnimationFrame(() => inputEl?.focus())
    return () => cancelAnimationFrame(id)
  })

  $effect(() => {
    const q = query.trim()
    if (!open) return
    if (!q) {
      results = []
      searched = false
      loading = false
      return
    }
    if (!serverList.length) {
      results = []
      searched = true
      loading = false
      return
    }

    loading = true
    let cancelled = false
    const timer = setTimeout(() => {
      searchSeriesGlobal($servers, q)
        .then((hits) => {
          if (cancelled) return
          results = Array.isArray(hits) ? hits : []
          searched = true
        })
        .catch((err) => {
          if (cancelled) return
          console.error('global search failed', err)
          results = []
          searched = true
        })
        .finally(() => {
          if (!cancelled) loading = false
        })
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  })

  function seriesTitle(s: any) {
    return s?.Infos?.title || s?.localTitle || 'Untitled'
  }

  function coverSrc(s: any) {
    const mediaId = s?.mediaId
    const seriesId = s?.id
    const server = mediaId ? $servers[mediaId] : undefined
    const cdnUrl = server?.urls?.[0] ?? ''
    if (!s?.Status?.hasCover || !seriesId || !cdnUrl) return PLACEHOLDER
    const v = s?.Infos?.refreshedAt ? new Date(s.Infos.refreshedAt).getTime() : 0
    const bust = v ? `?v=${v}` : ''
    let src = `${cdnUrl}/api/assets/cover/${seriesId}${bust}`
    if (cdnUrl.indexOf('http:') != -1) {
      const qs = [`upstream_addr=${cdnUrl.replace('http://', '')}`, v ? `v=${v}` : '']
        .filter(Boolean)
        .join('&')
      src = `https://proxy.nuread.direct.habysmol.com/api/assets/cover/${seriesId}?${qs}`
    }
    return src
  }

  function seriesHref(s: any) {
    const mediaId = s?.mediaId
    const libraryId = s?.libraryId
    const seriesId = s?.id
    if (!mediaId || !libraryId || !seriesId) return null
    return `/media/${mediaId}/library/${libraryId}/series/${seriesId}`
  }

  function selectSeries(s: any) {
    const href = seriesHref(s)
    if (!href) return
    open = false
    query = ''
    results = []
    searched = false
    goto(href)
  }

  function onOpenChange(next: boolean) {
    open = next
    if (!next) {
      query = ''
      results = []
      searched = false
      loading = false
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (!e.ctrlKey || e.metaKey || e.altKey || e.key.toLowerCase() !== 's') return
    e.preventDefault()
    open = true
  }
</script>

<svelte:window onkeydown={onKeydown} />

<Popover.Root bind:open onOpenChange={onOpenChange}>
  <Popover.Trigger>
    {#snippet child({ props })}
      <button
        {...props}
        type="button"
        class="group absolute left-52 top-1/2 flex h-9 w-[min(100%,22rem)] -translate-y-1/2
          items-center gap-2 rounded-md border border-latte-surface1/60 bg-latte-mantle/55 px-3
          text-left text-sm text-latte-text/55 shadow-sm backdrop-blur-sm
          transition-colors hover:border-latte-green/50 hover:text-latte-text
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-latte-green/60
          dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/55 dark:text-macchiato-text/55
          dark:hover:border-macchiato-green/50 dark:hover:text-macchiato-text
          dark:focus-visible:ring-macchiato-green/60
          max-sm:left-auto max-sm:right-28 max-sm:w-9 max-sm:justify-center max-sm:px-0
          sm:left-56"
        aria-label="Search series across all servers"
      >
        <SearchIcon class="size-4 shrink-0 opacity-70" strokeWidth={1.75} />
        <span class="min-w-0 flex-1 truncate max-sm:hidden">Search series…</span>
        <kbd
          class="pointer-events-none hidden shrink-0 items-center gap-0.5 rounded border
            border-latte-surface1/70 bg-latte-base/60 px-1.5 py-0.5 font-mono text-[10px]
            text-latte-text/50 sm:inline-flex
            dark:border-macchiato-surface1/70 dark:bg-macchiato-base/60 dark:text-macchiato-text/50"
        >
          Ctrl+S
        </kbd>
      </button>
    {/snippet}
  </Popover.Trigger>
  <Popover.Content
    align="start"
    sideOffset={8}
    class="w-[min(calc(100vw-1.5rem),28rem)] border-latte-surface1/60 bg-latte-mantle/95 p-0 shadow-lg
      backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
  >
    <Command.Root shouldFilter={false} label="Global series search" class="bg-transparent">
      <Command.Input
        bind:ref={inputEl}
        bind:value={query}
        placeholder="Search across all servers…"
        class="text-latte-text dark:text-macchiato-text"
      />
      <Command.List
        class="max-h-80 scrollbar-thin scrollbar-thumb-latte-surface1 scrollbar-track-latte-mantle
          dark:scrollbar-thumb-macchiato-surface1 dark:scrollbar-track-macchiato-crust"
      >
        {#if loading && query.trim()}
          <div
            class="flex items-center justify-center gap-2 px-3 py-8 text-sm
              text-latte-text/60 dark:text-macchiato-text/60"
          >
            <LoaderCircle class="size-4 animate-spin" strokeWidth={1.75} />
            Searching…
          </div>
        {:else if filtered.length === 0}
          <Command.Empty>{emptyHint}</Command.Empty>
        {:else}
          <Command.Group heading="Series">
            {#each filtered as s (s.mediaId + ':' + s.id)}
              {@const title = seriesTitle(s)}
              {@const mediaId = s.mediaId}
              {@const server = mediaId ? $servers[mediaId] : undefined}
              {@const libName = libraryName(mediaId, s.libraryId)}
              <Command.Item
                value={`${title} ${s.localTitle ?? ''} ${mediaId} ${libName ?? ''}`}
                class="gap-3 rounded-sm px-2 py-2"
                onSelect={() => selectSeries(s)}
              >
                <img
                  src={coverSrc(s)}
                  alt=""
                  class="h-12 w-8 shrink-0 rounded object-cover bg-latte-base dark:bg-macchiato-base"
                  loading="lazy"
                  onerror={(e) => {
                    const img = e.currentTarget as HTMLImageElement
                    if (img.src !== PLACEHOLDER) img.src = PLACEHOLDER
                  }}
                />
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-medium text-latte-text dark:text-macchiato-text">
                    {title}
                  </div>
                  <div
                    class="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs
                      text-latte-text/55 dark:text-macchiato-text/55"
                  >
                    <span class="inline-flex min-w-0 items-center gap-1">
                      <ServerIcon
                        class="size-3 shrink-0 text-latte-blue dark:text-macchiato-blue"
                        strokeWidth={1.75}
                      />
                      <span class="truncate">
                        {server
                          ? formatServerName(server.name, mediaId)
                          : `#${serverHash(mediaId ?? '')}`}
                      </span>
                    </span>
                    {#if libName}
                      <span class="shrink-0 opacity-50">·</span>
                      <span class="inline-flex min-w-0 items-center gap-1">
                        <LibraryIcon
                          class="size-3 shrink-0 text-latte-peach dark:text-macchiato-peach"
                          strokeWidth={1.75}
                        />
                        <span class="truncate">{libName}</span>
                      </span>
                    {/if}
                    {#if s.kind}
                      <span class="shrink-0 opacity-50">·</span>
                      <span class="inline-flex shrink-0 items-center gap-1">
                        <BookOpen
                          class="size-3 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
                          strokeWidth={1.75}
                        />
                        <span class="capitalize">{s.kind.toLowerCase?.() ?? s.kind}</span>
                      </span>
                    {/if}
                  </div>
                </div>
              </Command.Item>
            {/each}
          </Command.Group>
        {/if}
      </Command.List>
    </Command.Root>
  </Popover.Content>
</Popover.Root>
